import React, { useEffect, useState, useRef } from "react";
import { Chip } from "@mui/material";

const EmailTagInput = ({
    onChange,
    value,
    onEditTag,
    onDeleteTag,
    disabled,
    placeholder,
    className,
}) => {
    const parseValue = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === "string") return val.split(",").map((t) => t.trim()).filter(Boolean);
        return [];
    };

    const [tags, setTags] = useState(() => parseValue(value));
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef(null);
    const prevValueRef = useRef(parseValue(value));

    useEffect(() => {
        const parsed = parseValue(value);
        const prev = JSON.stringify(prevValueRef.current || []);
        const curr = JSON.stringify(parsed || []);
        if (prev !== curr) {
            setTags(parsed);
            prevValueRef.current = parsed;
        }
    }, [value]);

    const updateTags = (newTags) => {
        const cleanedTags = newTags.map((tag) => tag.trim()).filter((tag) => tag !== "");
        const uniqueTags = [...new Set(cleanedTags)];
        setTags(uniqueTags);
        prevValueRef.current = uniqueTags;
        if (onChange) {
            onChange(uniqueTags);
        }
    };

    const splitTokens = (text) => {
        if (!text) return [];
        return text.split(/[\s,;\t\n]+/).map((t) => t.trim()).filter(Boolean);
    };

    const addTags = (items) => {
        const list = Array.isArray(items) ? items : [items];
        const cleaned = list.map((t) => t.trim()).filter(Boolean);
        if (cleaned.length > 0) {
            const combined = [...tags, ...cleaned];
            updateTags(combined);
        }
    };

    const isSeparatorKey = (event) => {
        const key = event.key;
        return (
            key === "Enter" ||
            key === "Tab" ||
            key === " " ||
            key === "," ||
            event.code === "Space"
        );
    };

    const handleKeyDown = (event) => {
        if (isSeparatorKey(event)) {
            if (inputValue.trim() !== "") {
                const tokens = splitTokens(inputValue);
                if (tokens.length > 0) {
                    addTags(tokens);
                }
                setInputValue("");
                event.preventDefault();
            } else if (event.key === " " || event.code === "Space" || event.key === ",") {
                // Prevent leading blank space or comma
                event.preventDefault();
            }
            return;
        }
        handleBackspace(event);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        if (val.endsWith(" ") || val.endsWith(",")) {
            const trimmed = val.slice(0, -1).trim();
            if (trimmed !== "") {
                const tokens = splitTokens(trimmed);
                if (tokens.length > 0) {
                    addTags(tokens);
                }
            }
            setInputValue("");
            return;
        }
        if (val.includes(" ") || val.includes(",") || val.includes("\n") || val.includes("\t")) {
            const tokens = splitTokens(val);
            if (tokens.length > 1) {
                addTags(tokens);
                setInputValue("");
                return;
            }
        }
        setInputValue(val);
    };

    const handlePaste = (event) => {
        const pasteData = event.clipboardData ? event.clipboardData.getData("text") : "";
        if (!pasteData) return;
        const tokens = splitTokens(pasteData);
        if (tokens.length > 1) {
            event.preventDefault();
            addTags(tokens);
            setInputValue("");
        }
    };

    const handleEditTag = (tagToEdit, index) => {
        setInputValue(tagToEdit);
        updateTags(tags.filter((_, i) => i !== index));
    };

    const handleDeleteTag = (tagToDelete, index) => {
        updateTags(tags.filter((_, i) => i !== index));
    };

    const handleBackspace = (event) => {
        if (event.key === "Backspace" && inputValue === "" && tags.length > 0) {
            const lastTag = tags[tags.length - 1];
            updateTags(tags.slice(0, -1));
            setInputValue(lastTag);
            event.preventDefault();
        }
    };

    const handleBlur = () => {
        if (inputValue.trim() !== "") {
            const tokens = splitTokens(inputValue);
            if (tokens.length > 0) {
                addTags(tokens);
            }
            setInputValue("");
        }
    };

    return (
        <div
            className={`main_tag_input ${className || ""}`}
            onClick={() => inputRef.current && inputRef.current.focus()}
        >
            {tags.map((tag, index) => (
                <Chip
                    key={index}
                    label={tag}
                    onDelete={() => {
                        if (onDeleteTag) onDeleteTag(tag, index);
                        handleDeleteTag(tag, index);
                    }}
                    onClick={() => {
                        if (onEditTag) onEditTag(tag, index);
                        handleEditTag(tag, index);
                    }}
                />
            ))}
            <input
                type="text"
                ref={inputRef}
                value={inputValue}
                disabled={disabled}
                placeholder={tags.length === 0 ? placeholder : ""}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
            />
        </div>
    );
};

export default EmailTagInput;
